import { 
  Users, 
  TrendingUp, 
  Briefcase, 
  Target, 
  CheckCircle
} from 'lucide-react';
import { EmailButton, CONTACT_EMAIL, EMAIL_HREF } from '@/components/EmailButton';

const HomePage = () => {
  return (
    <div className="min-h-screen bg-white">
      
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-sm z-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-black" data-testid="logo">Koru Talent</h1>
            </div>
            <div className="hidden lg:flex items-center space-x-6">
              <a href="#services" className="text-gray-700 hover:text-burgundy-700 transition-colors">Services</a>
              <a href="#about" className="text-gray-700 hover:text-burgundy-700 transition-colors">About</a>
              <a href="#founder" className="text-gray-700 hover:text-burgundy-700 transition-colors">Our Founder</a>
              <a href="#testimonials" className="text-gray-700 hover:text-burgundy-700 transition-colors">Testimonials</a>
            </div>
            <EmailButton
              className="bg-burgundy-800 hover:bg-burgundy-900 text-white"
              data-testid="nav-email-btn"
            />
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8" data-testid="hero-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 animate-fadeInUp">
              <div className="space-y-4">
                <div className="inline-block px-4 py-2 bg-burgundy-50 rounded-full text-sm font-medium text-burgundy-900">
                  Professional Recruitment Services
                </div>
                <h1 className="text-5xl lg:text-6xl font-bold text-black leading-tight">
                  Connecting Talent,
                  <span className="block mt-2 text-burgundy-800">Building Futures</span>
                </h1>
                <p className="text-xl text-gray-600 leading-relaxed">
                  At Koru Talent, we specialise in linking skilled professionals with their ideal careers 
                  and empowering businesses to find outstanding candidates swiftly and efficiently.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <EmailButton
                  size="lg" 
                  className="bg-burgundy-800 hover:bg-burgundy-900 text-white text-lg px-8 py-6"
                  data-testid="hero-email-btn"
                />
              </div>
            </div>
            <div className="relative animate-fadeInUp animation-delay-200">
              <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.pexels.com/photos/4344860/pexels-photo-4344860.jpeg?auto=compress&cs=tinysrgb&w=800" 
                  alt="Professional recruitment meeting"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-r from-burgundy-900 to-burgundy-800 text-white" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-2xl sm:text-4xl font-bold mb-2" data-testid="stat-average-hire-value">&lt;5 Weeks</div>
              <div className="text-burgundy-200" data-testid="stat-average-hire-label">Average Successful Hire</div>
            </div>
            <div>
              <div className="text-2xl sm:text-4xl font-bold mb-2" data-testid="stat-roles-partnership-value">3+</div>
              <div className="text-burgundy-200" data-testid="stat-roles-partnership-label">Roles Per Partnership</div>
            </div>
            <div>
              <div className="text-2xl sm:text-4xl font-bold mb-2" data-testid="stat-experience-value">25+</div>
              <div className="text-burgundy-200" data-testid="stat-experience-label">Years' Experience</div>
            </div>
            <div>
              <div className="text-2xl sm:text-4xl font-bold mb-2" data-testid="stat-dedication-value">100%</div>
              <div className="text-burgundy-200" data-testid="stat-dedication-label">Dedicated to Your Search</div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50" data-testid="services-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-4">Our Services</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive recruitment solutions designed to connect you with the best candidates and career paths.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: <Users className="w-12 h-12" />,
                title: 'Candidate Sourcing',
                description: 'We specialise in finding skilled professionals perfectly matched to your company\'s needs.'
              },
              {
                icon: <TrendingUp className="w-12 h-12" />,
                title: 'Career Consulting',
                description: 'Offering expert advice to help individuals navigate and advance their professional journeys.'
              },
              {
                icon: <Briefcase className="w-12 h-12" />,
                title: 'Employer Services',
                description: 'Providing businesses with customised recruitment strategies to attract top talent efficiently.'
              },
              {
                icon: <Target className="w-12 h-12" />,
                title: 'Talent Pool Management',
                description: 'Maintaining a dynamic database of qualified candidates to meet your hiring demands promptly.'
              }
            ].map((service, index) => (
              <div 
                key={index} 
                className="bg-white p-8 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100 hover:border-burgundy-300 group"
                data-testid={`service-card-${index}`}
              >
                <div className="text-burgundy-800 mb-4 group-hover:scale-110 transition-transform">{service.icon}</div>
                <h3 className="text-xl font-bold text-black mb-3">{service.title}</h3>
                <p className="text-gray-600 leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-4 sm:px-6 lg:px-8" data-testid="about-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-4xl font-bold text-black">Bridging Exceptional Talent to Ideal Careers</h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Koru Talent is committed to matching skilled professionals with rewarding roles. 
                Our mission is to change lives by fostering meaningful connections founded on trust, 
                expertise, and dedication to long-term success.
              </p>
              <div className="space-y-4">
                {[
                  'Personalised career guidance and support',
                  'Access to exclusive job opportunities',
                  'Industry expertise across multiple sectors',
                  'Long-term partnership approach',
                  'Dedicated to your success'
                ].map((item, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-burgundy-800 flex-shrink-0 mt-1" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
              <EmailButton
                size="lg" 
                className="bg-burgundy-800 hover:bg-burgundy-900 text-white"
                data-testid="about-email-btn"
              />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1709715357520-5e1047a2b691?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMG1lZXRpbmd8ZW58MHx8fHwxNzY0ODcyOTIyfDA&ixlib=rb-4.1.0&q=85&w=800"
                alt="Professional business meeting"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section id="founder" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50" data-testid="founder-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Meet Our Founder</h2>
            <p className="text-xl text-gray-600">Leading with passion, expertise, and dedication</p>
          </div>
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="grid md:grid-cols-2 gap-8">
                <div className="aspect-square md:aspect-auto">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_koru-revamped/artifacts/tgt0sqp9_a1.png"
                    alt="Christian - Founder of Koru Talent"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-8 flex flex-col justify-center">
                  <h3 className="text-3xl font-bold text-black mb-2">Christian</h3>
                  <p className="text-burgundy-800 font-semibold mb-6">Founder & Director</p>
                  <p className="text-gray-600 leading-relaxed mb-6">
                    With over a decade of experience in recruitment and talent acquisition, Christian founded 
                    Koru Talent with a vision to transform how professionals find their ideal careers and how 
                    businesses discover exceptional talent.
                  </p>
                  <p className="text-gray-600 leading-relaxed mb-6">
                    His personalised approach and commitment to building lasting relationships have helped 
                    hundreds of candidates find their dream roles and numerous companies build their perfect teams.
                  </p>
                  <div className="flex flex-wrap gap-4">
                    <EmailButton
                      className="bg-burgundy-800 hover:bg-burgundy-900 text-white"
                      data-testid="founder-email-btn"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 px-4 sm:px-6 lg:px-8" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-4">What Our Clients Say</h2>
            <p className="text-xl text-gray-600">Trusted by professionals and businesses alike</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                quote: "It's been a genuine pleasure working with Christian. I was impressed with his positivity and his desire to help me become successful. Christian has provided me with valuable advice and guidance. I highly recommend working with him.",
                name: 'Zoe Banton',
                role: 'Recruitment Consultant'
              },
              {
                quote: "Koru Talent helped me find my dream job within weeks. Their personalised approach and industry knowledge made all the difference in my career transition.",
                name: 'James Mitchell',
                role: 'Software Engineer'
              },
              {
                quote: "As an employer, finding the right talent quickly is crucial. Koru Talent consistently delivers high-quality candidates that perfectly match our requirements.",
                name: 'Sarah Thompson',
                role: 'HR Director'
              }
            ].map((testimonial, index) => (
              <div 
                key={index} 
                className="bg-white p-8 rounded-xl shadow-sm border-2 border-gray-100 hover:border-burgundy-300 transition-all"
                data-testid={`testimonial-card-${index}`}
              >
                <div className="mb-6">
                  <div className="flex text-burgundy-700 space-x-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="w-5 h-5 fill-current" viewBox="0 0 20 20">
                        <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                      </svg>
                    ))}
                  </div>
                  <p className="text-gray-600 leading-relaxed italic">"{testimonial.quote}"</p>
                </div>
                <div>
                  <div className="font-bold text-black">{testimonial.name}</div>
                  <div className="text-gray-500 text-sm">{testimonial.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-burgundy-900 to-burgundy-800 text-white" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="text-4xl font-bold">Ready to Take the Next Step?</h2>
          <p className="text-xl text-burgundy-100">
            Whether you're looking for your next career opportunity or seeking the perfect candidate, 
            we're here to help. Let's start a conversation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <EmailButton
              size="lg" 
              className="bg-white hover:bg-gray-100 text-burgundy-900 text-lg px-8 py-6"
              data-testid="cta-email-btn"
            />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white py-12 px-4 sm:px-6 lg:px-8" data-testid="footer">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-xl font-bold mb-4 text-burgundy-400">Koru Talent</h3>
              <p className="text-gray-400">
                Connecting skilled professionals with their ideal careers.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><a href="#services" className="text-gray-400 hover:text-burgundy-400">Services</a></li>
                <li><a href="#about" className="text-gray-400 hover:text-burgundy-400">About</a></li>
                <li><a href="#founder" className="text-gray-400 hover:text-burgundy-400">Our Founder</a></li>
                <li><a href="#testimonials" className="text-gray-400 hover:text-burgundy-400">Testimonials</a></li>
                <li><a href={EMAIL_HREF} className="text-gray-400 hover:text-burgundy-400" data-testid="footer-email-link">Email Us</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2">
                <li>
                  <a href="/privacy-policy" className="text-gray-400 hover:text-burgundy-400">
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a href="/terms-conditions" className="text-gray-400 hover:text-burgundy-400">
                    Terms & Conditions
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Connect</h4>
              <div className="space-y-2">
                <a href={EMAIL_HREF} className="text-gray-400 hover:text-burgundy-400 block break-words" data-testid="footer-email-address">
                  {CONTACT_EMAIL}
                </a>
                <EmailButton
                  variant="outline" 
                  size="sm"
                  className="bg-transparent border-burgundy-600 text-burgundy-400 hover:bg-burgundy-900 hover:text-white mt-4"
                  data-testid="footer-email-btn"
                />
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>&copy; {new Date().getFullYear()} Koru Talent. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;